/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT, NgClass } from "@angular/common";
import {
	Component,
	computed,
	inject,
	Input,
	OnDestroy,
	OnInit,
	signal,
	TemplateRef,
	ViewChild
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from "ag-grid-community";
import { EChartsOption, EChartsType } from "echarts";
import {
	BehaviorSubject,
	combineLatest,
	defer,
	finalize,
	last,
	map,
	merge,
	of,
	repeat,
	Subject,
	switchMap,
	takeUntil,
	takeWhile,
	tap,
	throwError
} from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { CheckboxComponent } from "../../../shared/components/checkbox/checkbox.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../../shared/components/input/input.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { SwitchComponent } from "../../../shared/components/switch/switch.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import { UploadFileComponent } from "../../../shared/components/upload-file/upload-file.component";
import { PieChart } from "../../../shared/models/http/charts/pie-chart/pie-chart.model";
import { ApplicationError } from "../../../shared/models/ui/application-error.model";
import { RolePermission } from "../../../shared/models/ui/authorization.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { HardwareAssetsScopeMode } from "../../../shared/models/ui/hardware-assets-scope-mode.model";
import { HardwareAssetsScopeOrderBy } from "../../../shared/models/ui/hardware-assets-scope-orderby.model";
import { ReportingPeriodValue } from "../../../shared/models/ui/reporting-period.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DashboardHardwareAssetsService } from "../../../shared/services/dashboard-hardware-assets.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { StorageService } from "../../../shared/services/storage.service";
import { ToastService } from "../../../shared/services/toast.service";
import { COLORS, ORDER_BY_OPTIONS, PATHS } from "../../../shared/utils/constants";
import { CARBON_FOOTPRINT_BAR_CHART_OVERRIDES } from "../../../shared/utils/echarts/chart-constants";
import { getHardwareAssetsTotalEmissionOverridesForPieChart } from "../../../shared/utils/echarts/chart-utils";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../shared/utils/lazy-load";
import {
	getDefaultReportingPeriodValue,
	getReportingPeriodOptions
} from "../../../shared/utils/reporting-period";
import { mapBase64ToBlob, roundValueUpTo, saveAs } from "../../../shared/utils/utils";

@Component({
	selector: "globalictcc-hardware-assets",
	standalone: true,
	imports: [
		AsyncPipe,
		ButtonComponent,
		UploadFileComponent,
		DropdownComponent,
		CheckboxComponent,
		KpiComponent,
		ChartComponent,
		TableComponent,
		ChipsComponent,
		InputComponent,
		ReactiveFormsModule,
		SpinnerComponent,
		SwitchComponent,
		NgClass
	],
	templateUrl: "./hardware-assets.component.html",
	styleUrl: "./hardware-assets.component.scss"
})
export class HardwareAssetsComponent implements OnInit, OnDestroy {
	@Input() public viewMode: "baseline" | "realtime" = "baseline";
	@Input() public useServiceNow: boolean = false;
	@ViewChild("uploadDeviceList") public uploadDeviceListDialogContent: TemplateRef<unknown>;
	@ViewChild("rename") public renameDialogContent: TemplateRef<unknown>;
	@ViewChild("export") public exportDialogContent: TemplateRef<unknown>;
	@ViewChild("scopeChart") public scopeChart?: ChartComponent;

	private _pieChartTotalEmissions = signal<number>(0);
	public isLoading: boolean = false;
	public hasError: boolean = false;
	public isErrorLogOpened: boolean = false;
	public errorLogMessage: string = "Something went wrong";
	public exportForm = new FormGroup({
		generalInformation: new FormControl(false),
		manufacturerList: new FormControl(false),
		manufacturerPieChart: new FormControl(false),
		allCategory: new FormControl(false),
		percentageView: new FormControl(false),
		trueValueView: new FormControl(false)
	});
	public renameFileControl = new FormControl("");
	public fileControl = new FormControl("");
	public periodControl = new FormControl<ReportingPeriodValue>(getDefaultReportingPeriodValue());
	public periodOptions = getReportingPeriodOptions();
	public filesOptions: SelectOption[] = [];
	public categoriesControl = new FormControl([]);
	public orderByOptions: SelectOption[] = ORDER_BY_OPTIONS;
	public orderByControl = new FormControl(ORDER_BY_OPTIONS[0].value);
	public percentageViewRadioOptions: SelectOption[] = [
		{ label: "Current selection", value: "current" },
		{ label: "All manufacturers", value: "all" }
	];
	public trueValueViewRadioOptions: SelectOption[] = [
		{ label: "Current selection", value: "current" },
		{ label: "All manufacturers", value: "all" }
	];
	public uploadedFile = signal<File | undefined>(undefined);
	public selectedFile?: string;
	public sameNameErrorMessage = signal<string>("");
	public isUploadDisabled = computed(() => !!this.sameNameErrorMessage() || !this.uploadedFile());
	public hasConfirmed: boolean = false;
	public isServiceNowEnabled: boolean = false;
	public chips: ChipOption[] = [
		{ label: "PERCENTAGE VIEW", value: "PERCENTAGE", selected: true },
		{ label: "TRUE VALUE VIEW", value: "VALUE", selected: false }
	];
	public viewSwitchOptions: SelectOption[] = [
		{ label: "Baseline", value: "baseline", selected: true },
		{ label: "Realtime", value: "realtime" }
	];
	public totalEmissionTableHasError: boolean = false;
	public manufacturersHasError: boolean = false;
	public carbonFootprintBarChartColors: string[] = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100
	];
	public carbonFootprintBarChartOverrides = CARBON_FOOTPRINT_BAR_CHART_OVERRIDES;
	public emissionsByCountryOverTimeChartOverrides: EChartsOption = {
		series: { connectNulls: true }
	};
	public carbonFootprintByScopeBarChartOverrides: EChartsOption = {
		series: { stack: "bar" }
	};
	public totalEmissionPieChartColors: string[] = [];
	public co2ByInstallStatusPieChartColors: string[] = [];
	public pieChartOverrides = computed(() =>
		getHardwareAssetsTotalEmissionOverridesForPieChart(this._pieChartTotalEmissions())
	);
	public totalEmissionGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Manufacturer name", field: "manufacturer" },
			{ headerName: "Parts count", field: "parts" },
			{ headerName: "co2 kg co2e", field: "emissionValue" }
		],
		rowData: [],
		getRowStyle: params => {
			const percentage = (params.data.emissionValue / params.data.total) * 100;
			return {
				background: `linear-gradient(to right, ${COLORS.TEXT_GRAY} ${percentage}%, transparent ${percentage}%)`
			};
		},
		onGridReady: this._onTotalEmissionGridReady.bind(this)
	};
	public manufacturersGridOptions: GridOptions = {
		columnDefs: [{ headerName: "Manufacturer name", field: "manufacturer" }],
		rowData: [],
		rowSelection: { mode: "multiRow" },
		onGridReady: this._onCarbonFootprintGridReady.bind(this),
		onRowSelected: this._onManufacturerSelected.bind(this)
	};
	private _predefinedColors = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50
	];
	private readonly _document: Document = inject(DOCUMENT);
	public readonly grants = GRANTS.hwabl;
	public readonly permissions = RolePermission;
	public readonly authorizationService = inject(AuthorizationService);
	private readonly _dashboardHardwareAssetsService = inject(DashboardHardwareAssetsService);
	private readonly _dialogService = inject(DialogService);
	private readonly _storageService = inject(StorageService);
	private readonly _router = inject(Router);
	private readonly _toastService = inject(ToastService);
	private _totalEmissionGridApi?: GridApi<unknown>;
	private _manufacturersGridApi?: GridApi<unknown>;
	private _tenantId: string;
	private _fileId = "";
	private _scopeMode: HardwareAssetsScopeMode = "PERCENTAGE";
	private _selectedManufacturers: string[] = [];
	private _destroy$ = new Subject<void>();
	private _isPageTaskLoading = false;
	private readonly _loadingWidgets = new Set<string>();
	private readonly _fileId$ = new BehaviorSubject<string | undefined>(undefined);
	private readonly _manufacturerFilters$ = new BehaviorSubject<
		{ fileId: string; categories: string[] } | undefined
	>(undefined);
	private readonly _scopeFilters$ = new BehaviorSubject<
		| {
				fileId: string;
				mode: HardwareAssetsScopeMode;
				orderBy: HardwareAssetsScopeOrderBy;
				manufacturers: string[];
		  }
		| undefined
	>(undefined);
	private readonly _reload$ = new Subject<void>();
	private readonly _emissionsOverTimeReload$ = new Subject<void>();
	private readonly _manufacturerReload$ = new Subject<void>();
	private readonly _carbonFootprintReload$ = new Subject<void>();
	private readonly _scopeReload$ = new Subject<void>();
	private readonly _modelImpactReload$ = new Subject<void>();
	private readonly _regionImpactReload$ = new Subject<void>();
	private readonly _installStatusReload$ = new Subject<void>();
	private readonly _lifecycleReload$ = new Subject<void>();
	private readonly _countryReload$ = new Subject<void>();
	private _latestTotalEmissionRows: unknown[] = [];
	private _latestManufacturerRows: unknown[] = [];

	public readonly categories$ = lazyLoad(of(true), () =>
		this._dashboardHardwareAssetsService
			.getCategories(this._tenantId)
			.pipe(
				map(({ categories }) =>
					categories.map(category => ({ label: category, value: category }))
				)
			)
	).pipe(tap(state => this._trackWidgetLoading("categories", state)));
	public readonly generalInformation$ = lazyLoad(
		this._fileId$,
		fileId =>
			this._dashboardHardwareAssetsService.getGeneralInformation(fileId, this._tenantId),
		this._reload$
	).pipe(tap(state => this._trackWidgetLoading("generalInformation", state)));
	public readonly emissionsOverTime$ = lazyLoad(
		this._fileId$,
		fileId => this._dashboardHardwareAssetsService.getEmissionsOverTime(fileId, this._tenantId),
		merge(this._reload$, this._emissionsOverTimeReload$)
	).pipe(tap(state => this._trackWidgetLoading("emissionsOverTime", state)));
	public readonly totalEmissionByManufacturer$ = lazyLoad(
		this._manufacturerFilters$,
		filters =>
			combineLatest([
				this._dashboardHardwareAssetsService.getTotalEmissionForPieChart(
					filters.fileId,
					this._tenantId,
					filters.categories
				),
				this._dashboardHardwareAssetsService.getManufacturersEmission(
					filters.fileId,
					this._tenantId,
					filters.categories
				)
			]),
		merge(this._reload$, this._manufacturerReload$)
	).pipe(
		tap(state => {
			this._trackWidgetLoading("manufacturerEmission", state);
			this._updateManufacturerWidget(state);
		})
	);
	public readonly carbonFootprint$ = lazyLoad(
		this._fileId$,
		fileId => this._dashboardHardwareAssetsService.getCarbonFootprint(fileId, this._tenantId),
		merge(this._reload$, this._carbonFootprintReload$)
	).pipe(tap(state => this._trackWidgetLoading("carbonFootprint", state)));
	public readonly manufacturers$ = lazyLoad(
		this._fileId$,
		fileId => this._dashboardHardwareAssetsService.getManufacturers(fileId, this._tenantId),
		merge(this._reload$, this._scopeReload$)
	).pipe(
		tap(state => {
			this._trackWidgetLoading("manufacturers", state);
			this._updateManufacturersGrid(state);
		})
	);
	public readonly carbonFootprintByScope$ = lazyLoad(
		this._scopeFilters$,
		filters =>
			this._dashboardHardwareAssetsService.getCarbonFootprintByScope(
				filters.fileId,
				this._tenantId,
				filters.mode,
				filters.orderBy,
				filters.manufacturers
			),
		merge(this._reload$, this._scopeReload$)
	).pipe(tap(state => this._trackWidgetLoading("carbonFootprintByScope", state)));
	public readonly carbonFootprintByScopePercentage$ = lazyLoad(
		this._scopeFilters$,
		filters =>
			this._dashboardHardwareAssetsService.getCarbonFootprintByScope(
				filters.fileId,
				this._tenantId,
				"PERCENTAGE",
				filters.orderBy,
				filters.manufacturers
			),
		merge(this._reload$, this._scopeReload$)
	).pipe(tap(state => this._trackWidgetLoading("carbonFootprintByScope", state)));
	public readonly carbonFootprintByScopeValue$ = lazyLoad(
		this._scopeFilters$,
		filters =>
			this._dashboardHardwareAssetsService.getCarbonFootprintByScope(
				filters.fileId,
				this._tenantId,
				"VALUE",
				filters.orderBy,
				filters.manufacturers
			),
		merge(this._reload$, this._scopeReload$)
	).pipe(tap(state => this._trackWidgetLoading("carbonFootprintByScope", state)));
	public readonly modelImpact$ = lazyLoad(
		this._fileId$,
		fileId =>
			combineLatest([
				this._dashboardHardwareAssetsService.getEmissionsByModel(fileId, this._tenantId),
				this._dashboardHardwareAssetsService.getEmissionsByModelPerDevice(
					fileId,
					this._tenantId
				)
			]),
		merge(this._reload$, this._modelImpactReload$)
	).pipe(tap(state => this._trackWidgetLoading("modelImpact", state)));
	public readonly lifecycleEmissions$ = lazyLoad(
		this._fileId$,
		fileId =>
			this._dashboardHardwareAssetsService.getSavedLifecycleEmissions(fileId, this._tenantId),
		merge(this._reload$, this._lifecycleReload$)
	).pipe(tap(state => this._trackWidgetLoading("lifecycleEmissions", state)));
	public readonly emissionsByCountry$ = lazyLoad(
		this._fileId$,
		fileId =>
			this._dashboardHardwareAssetsService.getEmissionsByCountryOverTime(
				fileId,
				this._tenantId
			),
		merge(this._reload$, this._countryReload$)
	).pipe(tap(state => this._trackWidgetLoading("emissionsByCountry", state)));

	public readonly regionImpact$ = lazyLoad(
		this._fileId$,
		fileId =>
			combineLatest([
				this._dashboardHardwareAssetsService.getEmissionsByRegion(fileId, this._tenantId),
				this._dashboardHardwareAssetsService.getEmissionsByRegionPerDevice(
					fileId,
					this._tenantId
				)
			]),
		merge(this._reload$, this._regionImpactReload$)
	).pipe(tap(state => this._trackWidgetLoading("regionImpact", state)));
	public readonly installStatus$ = lazyLoad(
		this._fileId$,
		fileId =>
			this._dashboardHardwareAssetsService.getCo2ByInstallationStatusForPieChart(fileId),
		merge(this._reload$, this._installStatusReload$)
	).pipe(
		tap(state => {
			const slices = state.data?.chart?.PieChartSlice;
			if (!slices) return;
			this.co2ByInstallStatusPieChartColors = slices.map((_, index) =>
				index === slices.length - 1
					? COLORS.GREY_50
					: this._predefinedColors[index % this._predefinedColors.length]
			);
		})
	);
	private _trackWidgetLoading<T>(key: string, state: Loadable<T>) {
		queueMicrotask(() => {
			if (state.isLoading) this._loadingWidgets.add(key);
			else this._loadingWidgets.delete(key);
			this._syncPageLoading();
		});
	}

	private _setPageTaskLoading(isLoading: boolean) {
		this._isPageTaskLoading = isLoading;
		this._syncPageLoading();
	}

	private _syncPageLoading() {
		this.isLoading = this._isPageTaskLoading || this._loadingWidgets.size > 0;
	}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this.isServiceNowEnabled =
			this.useServiceNow || this._storageService.getServiceNowEnabled();
		this._dashboardHardwareAssetsService.setServiceNowOverride(this.isServiceNowEnabled);
		if (this.isServiceNowEnabled) {
			this._dashboardHardwareAssetsService.setServiceNowPeriod(
				this.periodControl.value ?? getDefaultReportingPeriodValue()
			);
		}
		this.viewSwitchOptions = [
			{ label: "Baseline", value: "baseline", selected: this.viewMode === "baseline" },
			{ label: "Realtime", value: "realtime", selected: this.viewMode === "realtime" }
		];
		this._fetchData().subscribe();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
		this._dashboardHardwareAssetsService.setServiceNowOverride(null);
	}

	/**
	 * Handles the grid ready event for the total emission grid.
	 *
	 * This method initializes the grid API reference and triggers the fetching
	 * of manufacturer emission data when the grid is ready.
	 *
	 * @param params - The event parameters containing the grid API and other information.
	 */
	private _onTotalEmissionGridReady(params: GridReadyEvent) {
		this._totalEmissionGridApi = params.api;
		if (!params.api.isDestroyed()) {
			params.api.setGridOption("rowData", this._latestTotalEmissionRows);
		}
	}

	/**
	 * Handles the grid ready event for the carbon footprint grid.
	 * Stores the grid API instance and triggers the initial fetch of manufacturers data.
	 *
	 * @param params - The event parameters containing the grid API.
	 */
	private _onCarbonFootprintGridReady(params: GridReadyEvent) {
		this._manufacturersGridApi = params.api;
		if (!params.api.isDestroyed()) {
			params.api.setGridOption("rowData", this._latestManufacturerRows);
		}
	}

	/**
	 * Handles the selection event for manufacturers in the data grid.
	 * Updates the `_selectedManufacturers` property with the list of manufacturers
	 * from the currently selected rows.
	 *
	 * @param params - The row selection event containing the API to access selected rows.
	 */
	private _onManufacturerSelected(params: RowSelectedEvent) {
		this._selectedManufacturers = params.api.getSelectedRows().map(row => row.manufacturer);
	}

	/**
	 * Handles the file change event by updating the internal file ID and triggering data fetching operations.
	 *
	 * @param fileId - The ID of the selected file, which can be a string or an array of strings.
	 *
	 * This method updates the `_fileId` property with the provided `fileId` (cast to a string),
	 * and then initiates parallel fetching of data, manufacturers' emissions, and manufacturers' information.
	 * The results of these asynchronous operations are combined and subscribed to, but the subscription is not managed or unsubscribed.
	 */
	public onFileChange(fileId: string | string[]) {
		if (Array.isArray(fileId)) return;
		this._setActiveFile(fileId);
	}

	/**
	 * Fetches hardware asset files and related dashboard data for the current tenant.
	 *
	 * This method performs the following steps:
	 * 1. Sets the loading state to true.
	 * 2. Retrieves the list of files associated with the tenant and maps them to options for selection.
	 * 3. Updates the component's file options, selection state, and form control based on the retrieved files.
	 * 4. If a file is selected, fetches additional dashboard data in parallel, including KPIs, categories, emission pie chart, carbon footprint, and carbon footprint by scope.
	 * 5. Resets the loading state when the operation is finalized.
	 *
	 * @returns An observable that emits the combined dashboard data if a file is selected, or completes immediately if not.
	 */
	private _fetchData() {
		this._setPageTaskLoading(true);

		if (this.isServiceNowEnabled) {
			this.filesOptions = [];
			this.hasConfirmed = true;
			this.selectedFile = undefined;
			this._fileId = "";
			this.fileControl.setValue("");

			this._setActiveFile("");
			return of([]).pipe(finalize(() => this._setPageTaskLoading(false)));
		}

		return this._dashboardHardwareAssetsService.getFiles(this._tenantId).pipe(
			map(({ files }) => (files || []).map(file => ({ label: file.name, value: file.id }))),
			tap({
				next: files => {
					this.filesOptions = files;
					this.hasConfirmed = !!files.length;
					if (!files.length) {
						this.selectedFile = undefined;
						this.fileControl.setValue("");
						this._setActiveFile(undefined);
						return;
					}

					const firstFile = files[0]?.value;
					const isFileIdNotPresent = !this.filesOptions.some(
						file => file.value === this._fileId
					);

					const selectedFileId =
						!this._fileId || isFileIdNotPresent ? firstFile : this._fileId;

					this.selectedFile = this.filesOptions.find(
						file => file.value === selectedFileId
					)?.label;

					this.fileControl.setValue(selectedFileId ?? "");
					this._setActiveFile(selectedFileId);
				},
				error: () => this._setPageTaskLoading(false)
			}),
			map(files => files),
			finalize(() => this._setPageTaskLoading(false))
		);
	}

	private _setActiveFile(fileId: string | undefined) {
		this._fileId = fileId ?? "";
		this._latestTotalEmissionRows = [];
		this._latestManufacturerRows = [];
		if (this._totalEmissionGridApi && !this._totalEmissionGridApi.isDestroyed()) {
			this._totalEmissionGridApi.setGridOption("rowData", []);
		}
		if (this._manufacturersGridApi && !this._manufacturersGridApi.isDestroyed()) {
			this._manufacturersGridApi.setGridOption("rowData", []);
		}
		this.selectedFile = this.filesOptions.find(file => file.value === fileId)?.label;
		this.fileControl.setValue(fileId ?? "", { emitEvent: false });
		this._fileId$.next(fileId);
		this._manufacturerFilters$.next(
			fileId !== undefined
				? { fileId, categories: (this.categoriesControl.value as string[]) ?? [] }
				: undefined
		);
		this._setScopeFilters(fileId);
	}

	private _setScopeFilters(fileId: string | undefined = this._fileId$.value) {
		this._scopeFilters$.next(
			fileId !== undefined
				? {
						fileId,
						mode: this._scopeMode,
						orderBy: this.orderByControl.value as HardwareAssetsScopeOrderBy,
						manufacturers: this._selectedManufacturers
					}
				: undefined
		);
	}

	private _updateManufacturerWidget(
		state: Loadable<[ChartResponse<PieChart>, { rowData: unknown[]; hasError: boolean }]>
	) {
		if (this._totalEmissionGridApi && !this._totalEmissionGridApi.isDestroyed()) {
			this._totalEmissionGridApi.setGridOption("loading", state.isLoading);
		}
		if (!state.data) {
			if (state.hasError) {
				this.totalEmissionTableHasError = true;
				this._latestTotalEmissionRows = [];
				this.totalEmissionPieChartColors = [];
				if (this._totalEmissionGridApi && !this._totalEmissionGridApi.isDestroyed()) {
					this._totalEmissionGridApi.setGridOption("rowData", []);
				} else {
					this.totalEmissionGridOptions.rowData = [];
				}
			}
			return;
		}

		const [pie, table] = state.data;
		this._latestTotalEmissionRows = table.rowData ?? [];
		this.totalEmissionTableHasError = table.hasError;
		if (this._totalEmissionGridApi && !this._totalEmissionGridApi.isDestroyed()) {
			this._totalEmissionGridApi.setGridOption("rowData", this._latestTotalEmissionRows);
		} else {
			this.totalEmissionGridOptions.rowData = this._latestTotalEmissionRows;
		}

		if (pie.hasError || !pie.chart?.PieChartSlice) return;
		this.totalEmissionPieChartColors = pie.chart.PieChartSlice.map((_, index, slices) =>
			index === slices.length - 1
				? COLORS.GREY_50
				: this._predefinedColors[index % this._predefinedColors.length]
		);
		this._pieChartTotalEmissions.set(
			roundValueUpTo(pie.chart.PieChartSlice.reduce((total, slice) => total + slice.value, 0))
		);
	}

	private _updateManufacturersGrid(state: Loadable<{ rowData: unknown[]; hasError: boolean }>) {
		if (this._manufacturersGridApi && !this._manufacturersGridApi.isDestroyed()) {
			this._manufacturersGridApi.setGridOption("loading", state.isLoading);
		}
		if (!state.data) return;
		if (state.hasError) {
			this.manufacturersHasError = true;
			this._latestManufacturerRows = [];
			if (this._manufacturersGridApi && !this._manufacturersGridApi.isDestroyed()) {
				this._manufacturersGridApi.setGridOption("rowData", []);
			} else {
				this.manufacturersGridOptions.rowData = [];
			}
			return;
		}

		this._latestManufacturerRows = state.data.rowData ?? [];
		this.manufacturersHasError = state.hasError || state.data.hasError;
		if (this._manufacturersGridApi && !this._manufacturersGridApi.isDestroyed()) {
			this._manufacturersGridApi.setGridOption("rowData", this._latestManufacturerRows);
		} else {
			this.manufacturersGridOptions.rowData = this._latestManufacturerRows;
		}
	}

	public onViewSwitch(targetView: string): void {
		if (targetView === "realtime" && this.viewMode !== "realtime") {
			this._router.navigateByUrl(`${PATHS.DASHBOARD}/${PATHS.HARDWARE_ASSETS_REALTIME}`);
			return;
		}

		if (targetView === "baseline" && this.viewMode !== "baseline") {
			this._router.navigateByUrl(`${PATHS.DASHBOARD}/${PATHS.HARDWARE_ASSETS}`);
		}
	}

	/**
	 * Fetches the total emissions data by combining the results of manufacturer emissions
	 * and total emission pie chart observables. Initiates both asynchronous operations
	 * and subscribes to their combined output.
	 *
	 * and updates the component's pie chart data and color scheme accordingly. It also handles
	 * errors by setting an error state on the chart data and ensures the loading state is reset
	 * when the operation completes.
	 *
	 * @returns An Observable that emits the total emission pie chart data.
	 */
	public fetchTotalEmissions() {
		this._manufacturerFilters$.next({
			fileId: this._fileId,
			categories: (this.categoriesControl.value as string[]) ?? []
		});
	}

	public fetchManufacturesEmission() {
		this._manufacturerReload$.next();
	}

	public fetchTotalEmissionPieChart() {
		this._manufacturerReload$.next();
	}

	public refreshInstallStatus() {
		this._installStatusReload$.next();
	}

	/**
	 * Initiates the process to fetch the carbon footprint data for hardware assets.
	 *
	 * This method calls the private `_fetchCarbonFootprint` method and subscribes to its observable,
	 * triggering the data retrieval process. The subscription is not handled here, so any emitted values,
	 * errors, or completion notifications are not processed within this method.
	 */
	public fetchCarbonFootprint() {
		this._carbonFootprintReload$.next();
	}

	/**
	 * Initiates the process of fetching the list of hardware manufacturers.
	 *
	 * This method calls the private `_fetchManufacturers` method and subscribes to its observable,
	 * triggering the retrieval of manufacturer data. The subscription is not stored, so any emitted
	 * values or errors are not handled here.
	 *
	 * @remarks
	 * This method is typically used to refresh or initially load the list of manufacturers
	 * associated with hardware assets.
	 */
	public fetchManufacturers() {
		this._scopeReload$.next();
	}

	/**
	 * Initiates the retrieval of carbon footprint data categorized by scope.
	 *
	 * This method calls the private `_fetchCarbonFootprintByScope` function and subscribes to its observable,
	 * triggering the data fetch process. Intended to be used for updating or loading carbon footprint information
	 * segmented by scope within the dashboard.
	 */
	public fetchCarbonFootprintByScope() {
		this._setScopeFilters();
		this._scopeReload$.next();
	}

	/**
	 * Handles the upload of a file by checking for duplicate file names and updating relevant state.
	 *
	 * If a file with the same name already exists in `filesOptions`, sets an error message and aborts the upload.
	 * Otherwise, clears any previous error message and sets the uploaded file.
	 *
	 * @param file - The file object selected for upload.
	 */
	public onFileUpload(file: File) {
		const fileName = file.name;

		if (this.filesOptions.find(file => file.label === fileName)) {
			this.sameNameErrorMessage.set(
				"This name is already in use by another report. Please enter a different name."
			);

			return;
		}

		this.sameNameErrorMessage.set("");
		this.uploadedFile.set(file);
	}

	/**
	 * Initiates the download of a hardware assets template by calling the corresponding service method.
	 * Subscribes to the observable returned by the service to trigger the download process.
	 *
	 * @remarks
	 * This method does not handle the response or errors explicitly; it simply subscribes to the observable.
	 */
	public onDownloadTemplate() {
		this._dashboardHardwareAssetsService
			.downloadTemplate()
			.pipe(
				map(blob => URL.createObjectURL(blob)),
				tap({
					next: (url: string) =>
						saveAs(this._document, url, "device_ingestion_template.xlsx")
				})
			)
			.subscribe();
	}

	/**
	 * Handles the confirmation action for uploading a hardware asset file.
	 *
	 * This method performs the following steps:
	 * 1. Checks if a file has been uploaded; if not, it exits early.
	 * 2. Sets the loading state to true.
	 * 3. Prepares the uploaded file as FormData and initiates the upload via the dashboard hardware assets service.
	 * 4. Upon successful upload, polls the server for the upload status every 5 seconds until the status is either "DONE" or "ERROR".
	 * 5. If an error status is received, sets error flags and logs the error message.
	 * 6. If the upload completes successfully, updates the loading and confirmation states and fetches the latest data.
	 *
	 * @remarks
	 * This method uses RxJS operators to handle asynchronous operations and polling.
	 *
	 * @throws {ApplicationError} If an error occurs while checking the upload status.
	 */
	public onConfirm() {
		if (!this.uploadedFile()) return;

		this._setPageTaskLoading(true);

		const fileFormData = new FormData();
		fileFormData.append("file", this.uploadedFile() as File);

		this._dashboardHardwareAssetsService
			.uploadFile(this._tenantId, fileFormData)
			.pipe(
				tap({
					next: ({ fileId }) => (this._fileId = fileId),
					finalize: () => (this.hasConfirmed = true)
				}),
				switchMap(({ fileId }) =>
					this._dashboardHardwareAssetsService
						.getUploadingStatus(fileId, this._tenantId)
						.pipe(
							repeat({ delay: 5000 }),
							takeWhile(
								({ status }) => status !== "DONE" && status !== "ERROR",
								true
							),
							last()
						)
				),
				switchMap(({ status, message }) => {
					this._setPageTaskLoading(false);

					if (status === "ERROR") {
						this.hasError = true;

						if (message) this.errorLogMessage = message;

						return throwError(
							() =>
								new ApplicationError(
									"Error while getting uploading status",
									this.constructor.name,
									this.onConfirm.name,
									false
								)
						);
					}

					return this._fetchData();
				}),
				finalize(() => this._setPageTaskLoading(false))
			)
			.subscribe();
	}

	/**
	 * Handles changes to the order by selection for hardware assets.
	 *
	 * Updates the `orderByControl` form control with the new order value and
	 * triggers a fetch to update the carbon footprint data based on the selected order.
	 *
	 * @param orderBy - The new order by value(s) selected by the user. Can be a string or an array of strings.
	 */
	public onOrderByChange(orderBy: string | string[]) {
		this.orderByControl.setValue(orderBy as HardwareAssetsScopeOrderBy);
		this._setScopeFilters();
	}

	/**
	 * Opens a confirmation dialog for uploading a device list.
	 *
	 * The dialog displays a custom title and content, and provides "Ok" and "Cancel" buttons.
	 * When the dialog is confirmed, the `onConfirm` method is invoked.
	 * The confirm button can be disabled based on the `isUploadDisabled` property.
	 * After the dialog is closed, any existing same-name error message is cleared.
	 * The subscription is automatically unsubscribed when the component is destroyed.
	 */
	public onUploadDeviceList() {
		this._dialogService
			.open({
				data: {
					type: "confirm",
					confirmBtnText: "Ok",
					closeBtnText: "Cancel",
					title: "Upload Device List",
					content: this.uploadDeviceListDialogContent,
					confirmAction: this.onConfirm.bind(this),
					isConfirmActionDisabled: this.isUploadDisabled
				}
			})
			.afterClosed()
			.pipe(tap({ next: () => this.sameNameErrorMessage.set("") }), takeUntil(this._destroy$))
			.subscribe();
	}

	/**
	 * Opens a confirmation dialog for renaming an item.
	 *
	 * The dialog displays a title, content, and two buttons ("Ok" and "Cancel").
	 * If the user confirms, the `_onConfirmRename` method is invoked.
	 *
	 * @remarks
	 * This method utilizes the injected `_dialogService` to present the dialog.
	 */
	public onRename() {
		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Ok",
				closeBtnText: "Cancel",
				title: "Rename",
				content: this.renameDialogContent,
				confirmAction: this._onConfirmRename.bind(this)
			}
		});
	}

	/**
	 * Handles the confirmation of a file rename action.
	 *
	 * Retrieves the new file name from the form control, validates it,
	 * and if valid, calls the service to rename the file. After renaming,
	 * it fetches the updated data to reflect changes in the UI.
	 *
	 * @private
	 */
	private _onConfirmRename() {
		const newName = this.renameFileControl.value as string;

		if (!newName) return;

		this._dashboardHardwareAssetsService
			.renameFile(this._fileId, this._tenantId, newName)
			.pipe(switchMap(() => this._fetchData()))
			.subscribe();
	}

	/**
	 * Opens a confirmation dialog to prompt the user before deleting the currently uploaded file.
	 * If the user confirms, the deletion action is executed via the bound `_onConfirmDelete` method.
	 *
	 * @remarks
	 * The dialog displays the name of the uploaded file (if available) in the confirmation message.
	 * The dialog provides "Yes, Delete" and "Cancel" options.
	 */
	public onDelete() {
		// To avoid breaking current state where we use `uploadedFile.name`
		let fileName = this.uploadedFile()?.name;

		if (!fileName) {
			fileName = this.filesOptions.find(file => file.value === this.fileControl.value)?.label;
		}

		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Yes, Delete",
				closeBtnText: "Cancel",
				title: `Are you sure you want to delete "${fileName}"?`,
				confirmAction: this._onConfirmDelete.bind(this)
			}
		});
	}

	/**
	 * Handles the confirmation of a file deletion.
	 *
	 * This method calls the dashboard hardware assets service to delete a file
	 * identified by `_fileId` and `_tenantId`. After successful deletion, it
	 * fetches the updated data. The operation is performed as an observable
	 * sequence, and the subscription is initiated within this method.
	 *
	 * @private
	 */
	private _onConfirmDelete() {
		this._dashboardHardwareAssetsService
			.deleteFile(this._fileId, this._tenantId)
			.pipe(switchMap(() => this._fetchData()))
			.subscribe();
	}

	/**
	 * Opens a confirmation dialog to export a report.
	 *
	 * The dialog presents the user with options to confirm or cancel the export action.
	 * If the user confirms, the `_onConfirmExportReport` method is invoked to handle the export logic.
	 *
	 * @remarks
	 * The dialog uses the `_dialogService` to display a modal with customizable content and button texts.
	 */
	public onExportReport() {
		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Export",
				closeBtnText: "Cancel",
				title: "Export Report",
				content: this.exportDialogContent,
				confirmAction: this._onConfirmExportReport.bind(this)
			}
		});
	}

	private _onConfirmExportReport() {}

	/**
	 * Initiates the download of hardware asset data as a CSV file.
	 *
	 * This method calls the dashboard hardware assets service to retrieve the CSV data
	 * in Base64 format, converts it to a Blob, creates a downloadable URL, and triggers
	 * the file download using the `saveAs` utility. The downloaded file is named
	 * "hardware_assets.csv".
	 *
	 * @remarks
	 * The method relies on the `_dashboardHardwareAssetsService` to fetch the CSV data,
	 * and uses helper functions `mapBase64ToBlob` and `saveAs` for processing and downloading.
	 */
	public onDownloadCsv() {
		defer(() => {
			this._toastService.success(
				"Download started",
				"Your CSV download has started successfully. Please wait patiently while the file is being prepared."
			);
			return this._dashboardHardwareAssetsService.downloadAsCsv(this._fileId, this._tenantId);
		})
			.pipe(
				map(csvBase64 => {
					const blob = mapBase64ToBlob(csvBase64, "text/csv");
					return URL.createObjectURL(blob);
				}),
				tap({
					next: (url: string) => {
						saveAs(this._document, url, "hardware_assets.csv");
						URL.revokeObjectURL(url); // Free the memory
					}
				})
			)
			.subscribe();
	}

	/**
	 * Handles the removal of the currently uploaded file.
	 *
	 * This method resets the `uploadedFile` property to `undefined`,
	 * effectively clearing any file that was previously set or uploaded.
	 */
	public onRemoveFile() {
		this.uploadedFile.set(undefined);
	}

	/**
	 * Handles the change event when renaming a file or report.
	 *
	 * Checks if the provided name already exists among the available file options.
	 * If the name is already present, sets an error message and marks the rename control with an error.
	 * Otherwise, clears any existing error messages and errors on the control.
	 *
	 * @param name - The new name entered by the user for the file or report. Can be a string, number, or null.
	 */
	public onRenameChange(name: string | number | null) {
		const isAlreadyPresent = this.filesOptions.map(file => file.label).includes(name as string);
		this.sameNameErrorMessage.set(
			isAlreadyPresent
				? "This name is already in use by another report. Please enter a different name."
				: ""
		);
		this.renameFileControl.setErrors(isAlreadyPresent ? { alreadyPresent: true } : null);
	}

	/**
	 * Handles changes to the hardware assets scope mode.
	 *
	 * Updates the internal `_scopeMode` property with the selected mode and triggers
	 * a fetch for the carbon footprint data based on the new scope.
	 *
	 * @param mode - The selected scope mode, which can be a string or number.
	 */
	public onModeChange(mode: string | number) {
		this._scopeMode = mode as HardwareAssetsScopeMode;
		this._setScopeFilters();
	}

	/**
	 * Exports the provided ECharts chart instance as a PNG image, temporarily removing any data zoom components
	 * to ensure the exported image does not include zoom controls. After exporting, the original chart options are restored.
	 *
	 * @param chartInstance - The ECharts chart instance to export. If not provided, the function returns early.
	 * @param imageName - Optional. The name to use for the exported image file. Defaults to "chart" if not specified.
	 */
	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();

		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({
				type: "png"
			});

			saveAs(this._document, chartAsPngUrl, imageName || "chart");

			chartInstance.setOption(originalOption, { notMerge: true });
		}, 1000);
	}

	public onApply() {
		const period = this.periodControl.value;
		if (period) this._dashboardHardwareAssetsService.setServiceNowPeriod(period);
		this._reload$.next();
	}

	public refresh(): void {
		this._reload$.next();
	}
}
